<div class="asset-request-container" [class.hide-horizontal-scroll]="hideHorizontalScroll">
  <!-- Expand Cell Template -->
  <ng-template #expandCell let-row>
    <button
      class="expand-btn"
      type="button"
      (click)="toggleRowExpansion(row)"
      [attr.aria-expanded]="isRowExpanded(row)"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        [style.transform]="isRowExpanded(row) ? 'rotate(90deg)' : 'rotate(0deg)'"
        [style.transition]="'transform 0.2s ease'"
      >
        <path
          d="M9 18L15 12L9 6"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </ng-template>

  <!-- Child Row Template -->
  <ng-template #childRow let-row>
    <tr class="table--child-row">
      <td [attr.colspan]="columns.length" class="table--child-cell">
        <div class="child-row-content">
          <div class="child-row-grid">
            <div class="child-row-item" *ngFor="let detail of getUserDetailEntriesForRow(row)">
              <span class="child-row-label">{{ detail.label }}</span>
              <span class="child-row-value">{{ detail.value }}</span>
            </div>
            <div class="child-row-item" *ngIf="getStatusActorEntry(row) as actor">
              <span class="child-row-label">{{ actor.label }}</span>
              <span class="child-row-value">{{ actor.value }}</span>
            </div>
            <div class="child-row-item child-row-item--purpose">
              <span class="child-row-label">Purpose</span>
              <span class="child-row-value">{{ row.purpose || '-' }}</span>
            </div>
          </div>
        </div>
      </td>
    </tr>
  </ng-template>

  <app-filter-panel
    *ngIf="showFilters"
    [fields]="filterFields"
    [defaultExpanded]="false"
    (filterChange)="onFilterChange($event)"
    (clearFilters)="onClearFilters()"
  ></app-filter-panel>

  <div *ngIf="enableRequestStatusTabs" class="request-status-toolbar">
    <div class="request-status-tabs" role="tablist" aria-label="Request status filters">
      <button
        *ngFor="let tab of requestStatusTabs"
        type="button"
        class="request-status-tabs__btn"
        [class.request-status-tabs__btn--active]="selectedRequestStatus === tab.value"
        [attr.aria-selected]="selectedRequestStatus === tab.value"
        (click)="setRequestStatusTab(tab.value)"
      >
        {{ tab.label }}
      </button>
    </div>
    <div class="request-status-toolbar__actions">
      <button
        *ngIf="showExportButton"
        type="button"
        class="request-status-toolbar__export-btn"
        aria-label="Export table"
        (click)="onExport()"
      >
        <span>Export</span>
        <svg
          class="request-status-toolbar__export-icon"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      </button>
      <ng-content select="[requestStatusToolbarActions]"></ng-content>
    </div>
  </div>

  <!-- Table -->
  <app-table
    [columns]="columns"
    [data]="filteredAssetRequestsData"
    [scrollHeight]="'400px'"
    [loading]="loading"
    [pagination]="effectivePagination"
    [emptyMessage]="emptyMessage"
    [childRowTemplate]="childRow"
    [isRowExpanded]="getIsRowExpanded()"
    [initialSortColumn]="serverSortUiActive ? currentSort.field : null"
    [initialSortDirection]="serverSortUiActive ? currentSort.order : 'asc'"
    [striped]="true"
    [bordered]="true"
    [hoverable]="true"
    [showExportButton]="showExportButton && !enableRequestStatusTabs"
    (export)="onExport()"
    (pageChange)="onPageChange($event)"
    (pageSizeChange)="onPageSizeChange($event)"
    (sortChange)="onSortChange($event)"
  >
    <!-- Requested At / Expires At / Expired At: date with subtle timestamp -->
    <ng-template #requestDateCell let-row>
      <span class="request-date-cell">
        <span class="request-date-cell__date">{{ getRequestDateCellValue(row).date }}</span>
        <span class="request-date-cell__time" *ngIf="getRequestDateCellValue(row).time">{{ getRequestDateCellValue(row).time }}</span>
      </span>
    </ng-template>

    <!-- Purpose column: ellipsis, click to show full in modal -->
    <ng-template #purposeCell let-row>
      <span
        class="purpose-cell-text"
        [title]="row.purpose || ''"
        (click)="openPurposeModal(row.purpose)"
      >{{ row.purpose || '-' }}</span>
    </ng-template>

    <!-- Request Status chip -->
    <ng-template #statusCell let-row>
      <span [ngClass]="getStatusChipClass(row.requestStatus)">{{ row.requestStatus }}</span>
    </ng-template>

    <!-- Custom Action Column -->
    <ng-template #actionCell let-row>
      <div class="action-cell-wrapper">
        <button 
          class="action-btn" 
          appActionDropdownTrigger
          (click)="toggleActionDropdown($event, row)"
          [class.active]="isDropdownOpen(row)"
          type="button"
        >
          <span>Action</span>
        </button>
        
        <!-- Dropdown Menu -->
        <div 
          class="action-dropdown-menu" 
          *ngIf="isDropdownOpen(row)" 
          [attr.data-dropdown-id]="getActionDropdownRowId(row)"
          (click)="$event.stopPropagation()"
        >
          <ul class="dropdown-list">
            <li class="dropdown-item" *ngIf="showAssetRequestAction('view')">
              <button class="dropdown-link" (click)="handleAction('view', row)">View</button>
            </li>
            <li class="dropdown-item" *ngIf="showAssetRequestAction('accept')">
              <button
                class="dropdown-link"
                [disabled]="isApproveRejectDisabled(row)"
                (click)="handleAction('accept', row)"
              >
                Accept
              </button>
            </li>
            <li class="dropdown-item" *ngIf="showAssetRequestAction('reject')">
              <button
                class="dropdown-link dropdown-link--danger"
                [disabled]="isApproveRejectDisabled(row)"
                (click)="handleAction('reject', row)"
              >
                Reject
              </button>
            </li>
            <li class="dropdown-item" *ngIf="canConverse(row)">
              <button class="dropdown-link" (click)="handleAction('conversation', row)">
                Conversation
              </button>
            </li>
          </ul>
        </div>
      </div>
    </ng-template>
  </app-table>

  <!-- Purpose full content modal -->
  <app-modal
    [(visible)]="showPurposeModal"
    header="Purpose"
    [width]="'480px'"
    [showFooter]="true"
    [showConfirmButton]="false"
    [showCancelButton]="true"
    cancelLabel="Close"
    (onCancel)="showPurposeModal = false"
  >
    <div class="purpose-modal-content">{{ purposeModalText }}</div>
  </app-modal>
  
  <!-- Approve asset request modal -->
  <app-modal
    [(visible)]="isApproveModalVisible"
    header="Approve Asset Request"
    [width]="'520px'"
    [modal]="true"
    [closable]="true"
    confirmLabel="Approve"
    cancelLabel="Cancel"
    [confirmButtonDisabled]="!expirationDate?.startDate || !isExpiryValid()"
    (onConfirm)="onApproveConfirm()"
    (onCancel)="closeApproveModal()"
    (onClose)="closeApproveModal()"
  >
    <div class="approve-asset-modal approve-asset-modal-content">
      <div class="approve-asset-modal__info">
        <span class="approve-asset-modal__icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </span>
        <p class="approve-asset-modal__description">
          Specify until when this access request should remain valid. The requester will have access to the asset until the selected date.
        </p>
      </div>
      <div class="approve-asset-modal__field">
        <label class="approve-asset-modal__label">
          Expiry At <span class="approve-asset-modal__required">*</span>
        </label>
        <div class="approve-asset-modal__date-time-row">
          <app-date-range-picker
            [(ngModel)]="expirationDate"
            [singleDateMode]="true"
            [minDate]="minDate"
            [disableFutureDates]="false"
            placeholder="Select date"
            styleClass="approve-asset-modal-date-picker"
          ></app-date-range-picker>
        </div>
        <span class="approve-asset-modal__hint">Access will be valid until this date</span>
      </div>
    </div>
  </app-modal>
</div>



